import { useMemo } from "react";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import type { PlatformComponentProps } from "@b2bportal/core-types";
import { Dialog, DialogContent, Icon } from "@tenant/ui";
import clsx from "clsx";
import DOMPurify from "dompurify";
import styles from "./styles.module.scss";

export interface ErrorContentProps {
  title: string;
  subtitle: string;
  iconName?: IconNameEnum;
  primaryButtonText: string;
  secondaryButtonText?: string;
  primaryOnClick: () => void;
  secondaryOnClick?: () => void;
  onClose?: () => void;
}

export interface ErrorModalProps
  extends ErrorContentProps,
    PlatformComponentProps {
  open: boolean;
}

export const ErrorModal: React.FC<ErrorModalProps> = ({
  primaryButtonText,
  primaryOnClick,
  secondaryButtonText,
  secondaryOnClick,
  title,
  subtitle,
  iconName,
  open,
  className,
  onClose,
}) => {
  const [block, cn] = useModuleBEM(styles, "ErrorModal");
  const hasSecondaryButton = secondaryButtonText && secondaryOnClick;

  const buttons = useMemo(
    () => [
      {
        buttonText: primaryButtonText,
        onClick: primaryOnClick,
        defaultStyle: "primary",
      },
      ...(hasSecondaryButton
        ? [
            {
              buttonText: secondaryButtonText,
              onClick: secondaryOnClick,
              defaultStyle: "secondary",
            },
          ]
        : []),
    ],
    [
      primaryButtonText,
      secondaryButtonText,
      primaryOnClick,
      secondaryOnClick,
      hasSecondaryButton,
    ]
  );

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogContent>
        <div className={clsx(block, className)}>
          <Icon
            className={cn("icon")}
            iconName={iconName || IconNameEnum.error}
            useCurrentColor={false}
          />
          <span className={cn("title")}>{title}</span>
          <span
            className={cn("subtitle")}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(subtitle) }}
          />

          {buttons.map((button) => (
            <button
              key={button.buttonText}
              className={cn(`button-${button.defaultStyle}`)}
              {...button}
            >
              {button.buttonText}
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
